import { FarmRiskType } from '@ourbranch/lookups';

// #32526: farm mortgages are stored per-structure on `farmLocations[].risks[].building.mortgageDetails`
// (the representation the dec/docs read), not on the legacy flat `farm.mortgageDetails`. The staff-fe
// mortgage form keeps a single flat "Mortgage lender information" list where each mortgage carries a
// form-only `appliesToStructure` (a `locationId::riskId` ref chosen in the "Applies to structure"
// dropdown). These helpers convert between that flat form list and per-building storage.

// Structures that can carry a mortgage: owned dwellings (FO-1/2/3) and FO-6 structures
// (barns/outbuildings). FO-4 renters (tenant contents) and scheduled/unscheduled inland marine
// are not mortgageable.
export const MORTGAGEABLE_RISK_TYPES = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo6Structures
];

export const isMortgageableRiskType = (type) => MORTGAGEABLE_RISK_TYPES.includes(type);

// Composite id used by the "Applies to structure" dropdown and to route a mortgage back to its
// building. Mirrors the `locationId::riskId` convention used elsewhere on the farm form.
export const structureRefId = (locationId, riskId) => `${locationId}::${riskId}`;

// #32526 (reopen): a mortgage may instead apply to a specific Related Private Structure (RPS) —
// a pool, a shed — which is a schedule-coverage sub-item, not a risk. RPS items have no `building`
// of their own, so the mortgage is still stored on the parent risk's `building.mortgageDetails`; the
// chosen item id is persisted alongside it as `appliesToRelatedStructureId` so the association
// round-trips and prints on the dec. The dropdown identifies an RPS with a 3-part composite ref.
export const rpsRefId = (locationId, riskId, itemId) => `${locationId}::${riskId}::${itemId}`;

const RELATED_PRIVATE_STRUCTURES_COVERAGE_TYPE = 'RELATED_PRIVATE_STRUCTURES';

// The Related Private Structure items under a risk (schedule-coverage sub-items). Mirrors the source
// getRpsOptions reads in farm-helpers.js so the mortgage dropdown lists exactly the RPS section.
const getRiskRpsItems = (risk) =>
  (risk?.coverages?.scheduleCoverages || [])
    .filter((sc) => sc?.coverageType === RELATED_PRIVATE_STRUCTURES_COVERAGE_TYPE)
    .flatMap((sc) => sc.items || []);

// RPS item display label — same precedence as getRpsOptions (extendedDetails.name, then description).
const rpsItemLabel = (item) => item?.extendedDetails?.name || item?.description || item?.id;

/**
 * Builds the "Applies to structure" dropdown options for the mortgage form: the subset of the
 * given risk options that correspond to mortgageable structures (owned dwellings + FO-6 barns).
 * Shared by the offer and policy-details render paths.
 * @param {Array} farmLocations - the form's farmLocations
 * @param {Array} riskOptions - `getRiskOptions(...)` output ({ id: 'loc::risk', value })
 */
export const getMortgageStructureOptions = (farmLocations, riskOptions) => {
  // Map each mortgageable risk ref → its risk object so we can append that structure's RPS items
  // directly after it in the dropdown. Deleted locations are skipped.
  const mortgageableRisksByRef = new Map();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (isMortgageableRiskType(risk.type)) {
        mortgageableRisksByRef.set(structureRefId(location.id, risk.id), risk);
      }
    }
  }

  const options = [];
  for (const option of riskOptions || []) {
    const risk = mortgageableRisksByRef.get(option.id);
    if (!risk) {
      continue; // non-mortgageable structure (inland marine, FO-4 renters) or a deleted location
    }
    options.push(option);
    // #32526 (reopen): each Related Private Structure under this structure is selectable too, labeled
    // "<parent structure> — <RPS name>" and identified by a 3-part `loc::risk::item` ref.
    for (const item of getRiskRpsItems(risk)) {
      options.push({ id: `${option.id}::${item.id}`, value: `${option.value} — ${rpsItemLabel(item)}` });
    }
  }
  return options;
};

/**
 * Flattens per-building mortgages into a single form list, tagging each with the form-only
 * `appliesToStructure` ref so the dropdown can show/edit the association. Skips deleted locations.
 */
export const flattenFarmMortgagesForForm = (farmLocations) => {
  const flat = [];
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      for (const mortgage of risk.building?.mortgageDetails || []) {
        // The persisted RPS tag (if any) is not surfaced in the form object — the dropdown drives off
        // `appliesToStructure` alone, and applyFarmMortgagesToLocations re-derives the tag from it. A
        // tagged mortgage flattens to a 3-part RPS ref; an untagged one to the plain structure ref.
        const { appliesToRelatedStructureId, ...rest } = mortgage || {};
        const appliesToStructure = appliesToRelatedStructureId
          ? rpsRefId(location.id, risk.id, appliesToRelatedStructureId)
          : structureRefId(location.id, risk.id);
        flat.push({ ...rest, appliesToStructure });
      }
    }
  }
  return flat;
};

/**
 * Collects the mortgageable structures (owned dwellings + FO-6 barns) that carry a building, in
 * document order, tagging dwellings so callers can prefer them. Skips deleted locations.
 */
const collectMortgageableStructures = (farmLocations) => {
  const structures = [];
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (risk?.building && isMortgageableRiskType(risk.type)) {
        structures.push({
          ref: structureRefId(location.id, risk.id),
          // FO-1/2/3 are dwellings; FO-6 structures (barns/outbuildings) are the fallback.
          isDwelling: risk.type !== FarmRiskType.Fo6Structures
        });
      }
    }
  }
  return structures;
};

// Given the collected mortgageable structures, the default a mortgage attaches to: the first owned
// dwelling, falling back to the first FO-6 structure. Mirrors the IMT importer's "first dwelling;
// fallback FO-6" convention. Null when there's no mortgageable structure.
const primaryRefFromStructures = (structures) =>
  (structures.find((structure) => structure.isDwelling) || structures[0])?.ref ?? null;

/**
 * The default structure a mortgage attaches to when the agent doesn't pick one (or picks a
 * structure that no longer exists): the first owned dwelling, falling back to the first FO-6
 * structure. Returns null when the farm has no mortgageable structure (e.g. an FO-4 renters-only
 * policy) — nowhere to place a mortgage.
 */
export const getPrimaryMortgageableStructureRef = (farmLocations) =>
  primaryRefFromStructures(collectMortgageableStructures(farmLocations));

/**
 * Distributes the flat form mortgage list back into each structure's `building.mortgageDetails`,
 * stripping the form-only `appliesToStructure`. Returns a NEW farmLocations array. Every building is
 * rewritten (buildings with no mortgage get an empty array) so removals and re-assignments persist.
 *
 * A mortgage is NEVER silently dropped: if it has no `appliesToStructure` — or points at a structure
 * that no longer exists — it falls back to the primary mortgageable structure (first dwelling, then
 * FO-6). Previously such a mortgage was discarded, so a mortgagee an agent added without touching the
 * easily-missed "Applies to structure" dropdown vanished on the next Update Offer and never printed
 * (#32607). The only case where a mortgage can't be placed is a farm with no mortgageable structure.
 */
export const applyFarmMortgagesToLocations = (farmLocations, flatMortgages) => {
  // Collect once; derive both the valid-ref set and the primary fallback from it.
  const structures = collectMortgageableStructures(farmLocations);
  const validRefs = new Set(structures.map((structure) => structure.ref));
  const primaryRef = primaryRefFromStructures(structures);

  // Valid RPS item ids per mortgageable risk ref — used to keep an RPS tag only when the item still
  // exists under the structure the mortgage lands on (a removed pool/shed drops the tag). (#32526)
  const rpsItemIdsByRiskRef = new Map();
  for (const location of farmLocations || []) {
    if (location?.isDeleted) {
      continue;
    }
    for (const risk of location.risks || []) {
      if (isMortgageableRiskType(risk.type)) {
        rpsItemIdsByRiskRef.set(
          structureRefId(location.id, risk.id),
          new Set(getRiskRpsItems(risk).map((item) => item.id))
        );
      }
    }
  }

  const byStructure = new Map();
  for (const mortgage of flatMortgages || []) {
    // Strip the form-only ref AND any stale persisted tag; the tag is re-derived from the ref below.
    const { appliesToStructure, appliesToRelatedStructureId: _staleTag, ...rest } = mortgage || {};

    // An RPS ref is `loc::risk::item`; a structure ref is `loc::risk`. The mortgage lives on the risk's
    // building either way — an RPS has no building — so route by the risk part.
    const parts = (appliesToStructure || '').split('::');
    const riskRef = parts.length >= 2 ? structureRefId(parts[0], parts[1]) : '';
    const rpsItemId = parts.length >= 3 ? parts[2] : '';

    const ref = validRefs.has(riskRef) ? riskRef : primaryRef;
    if (!ref) {
      // No mortgageable structure exists — nowhere to attach it.
      continue;
    }

    // Keep the RPS association only when the referenced item still exists under the target structure.
    const stored =
      rpsItemId && rpsItemIdsByRiskRef.get(ref)?.has(rpsItemId)
        ? { ...rest, appliesToRelatedStructureId: rpsItemId }
        : { ...rest };

    if (!byStructure.has(ref)) {
      byStructure.set(ref, []);
    }
    byStructure.get(ref).push(stored);
  }

  return (farmLocations || []).map((location) => ({
    ...location,
    risks: (location.risks || []).map((risk) => {
      // Scheduled/unscheduled inland-marine risks have no building — leave them untouched.
      if (!risk.building) {
        return risk;
      }
      const ref = structureRefId(location.id, risk.id);
      return { ...risk, building: { ...risk.building, mortgageDetails: byStructure.get(ref) || [] } };
    })
  }));
};
